import React from "react";

import { useNavigation } from "@refinedev/core";
import { ColumnDef } from "@tanstack/react-table";
import dayjs from "dayjs";

import { ColoredBadge } from "@/components/common/colored-badge";
import { UserAvatar } from "@/components/common/user-avatar";
import { ShowButton } from "@/components/refine-ui/buttons/show";
import { makeTooltip } from "@/components/ui/tooltip";
import { useMembers } from "@/contexts/member-context";
import {
  ENRICHMENT_HITL_REASON_OPTIONS,
  ENRICHMENT_OUTCOME_OPTIONS,
  SCRAPE_HITL_REASON_OPTIONS,
  SCRAPE_OUTCOME_OPTIONS,
} from "@/lib/tina/constants";
import { HitlReasonBadge } from "@/pages/tina/components/hitl-reason-badge";
import { ItemStatusBadge } from "@/pages/tina/components/item-status-badge";
import {
  AttemptsCell,
  EnrichmentEntityCell,
  EnrichmentModeCell,
  EnrichmentSourceCell,
  ItemUrlFallback,
  ParksCell,
} from "@/pages/tina/items/hooks/enrichment-columns";
import {
  asRecord,
  contentTypeLabel,
  discoveredUrlLabel,
  firstText,
  isDiscoverSeedItem,
  itemDiscoveredCount,
  itemLineageLabel,
  itemSourceTitle,
  itemSourceUrl,
  urlTitle,
} from "@/pages/tina/items/item-context";
import { triggeredByLabel } from "@/pages/tina/lib/run-attribution";
import type { PipelineItem } from "@/types/tina";

const confidenceTint = (value: number): string => {
  if (value >= 0.8)
    return "bg-emerald-500/15 text-emerald-700 dark:text-emerald-300";
  if (value >= 0.5) return "bg-amber-500/15 text-amber-700 dark:text-amber-300";
  return "bg-red-500/15 text-red-700 dark:text-red-300";
};

// Only fields the orchestrator's /events endpoint can filter (exact match) are
// exposed as working `select` filters; the rest disable their column filter so
// there is no dead UI (the Tina data provider forwards only `eq` filters).
const STATUS_OPTIONS = [
  { label: "Pending", value: "pending" },
  { label: "In progress", value: "in_progress" },
  { label: "Waiting on primary", value: "waiting_on_primary" },
  { label: "HITL", value: "hitl" },
  { label: "Done", value: "done" },
  { label: "Failed", value: "failed" },
  { label: "Failed (retryable)", value: "failed_retryable" },
  { label: "Skipped", value: "skipped" },
  { label: "Duplicate", value: "duplicate" },
  { label: "Cancelled", value: "cancelled" },
];

/**
 * `default` is the scrape column set (site / URL / context / stage).
 * `enrichment` swaps the four constant-or-meaningless columns for the job axes.
 */
export type ItemsColumnVariant = "default" | "enrichment";

/**
 * `site` / `url` / `context` — the three leading scrape columns.
 *
 * Module-level because none of them closes over a hook value, which keeps the
 * memo below dependent on `[members, variant]` alone.
 */
const SCRAPE_LEADING_COLUMNS: ColumnDef<PipelineItem>[] = [
  {
    id: "site",
    accessorKey: "site",
    header: "Site",
    cell: ColoredBadge,
    // Backend filters site by exact name only; use the Country filter for
    // geographic narrowing. Disable the (free-text) column filter.
    enableColumnFilter: false,
  },
  {
    id: "url",
    accessorKey: "url",
    header: "URL",
    cell: ({ row }) => {
      const url = row.original.url;
      // `url === null` is a discover seed, an enrichment job, or neither —
      // three different things that all used to render "discover-seed".
      if (!url) return <ItemUrlFallback item={row.original} />;
      if (URL.canParse(url)) {
        return makeTooltip(
          <a
            href={url}
            target="_blank"
            rel="noreferrer"
            onClick={(e) => e.stopPropagation()}
            className="text-blue-500 hover:underline truncate max-w-[280px] block text-xs"
          >
            {url}
          </a>,
          url,
        );
      }
      return makeTooltip(
        <span className="text-xs truncate max-w-[280px] block">{url}</span>,
        url,
      );
    },
    enableColumnFilter: false,
  },
  {
    id: "context",
    header: "Context",
    cell: ({ row }) => {
      const context = buildItemContext(row.original);
      if (!context) return <span className="text-muted-foreground">—</span>;
      const title = (
        <span className="block max-w-[260px] truncate text-xs font-medium">
          {context.title}
        </span>
      );
      return (
        <div className="min-w-0">
          {context.href ? (
            <a
              href={context.href}
              target="_blank"
              rel="noreferrer"
              onClick={(e) => e.stopPropagation()}
              className="text-blue-500 hover:underline"
            >
              {title}
            </a>
          ) : (
            title
          )}
          {context.subtitle && (
            <span className="block max-w-[260px] truncate text-[11px] text-muted-foreground">
              {context.subtitle}
            </span>
          )}
        </div>
      );
    },
    enableSorting: false,
    enableColumnFilter: false,
  },
];

/**
 * `entity` / `mode` / `source` — what replaces them on a job row.
 *
 * **None of the three carries a column filter, and that is deliberate.**
 * The entity filter is the toolbar's content-type select, which emits
 * the `content_type=enrichment_<entity>` server param through `buildItemsFilters`
 * into `filters.permanent`; a column filter over the same field could never
 * override a permanent entry (permanent wins in Refine's `unionFilters`), so it
 * would be an un-clearable control. `mode` and `source` have no server param at
 * all — `GET /events` does not accept them and the data provider only forwards
 * bare `eq` filters — so they ship as honest display columns rather than dead UI.
 */
const ENRICHMENT_LEADING_COLUMNS: ColumnDef<PipelineItem>[] = [
  {
    id: "entity",
    header: "Entity",
    cell: ({ row }) => <EnrichmentEntityCell item={row.original} />,
    enableSorting: false,
    enableColumnFilter: false,
  },
  {
    id: "mode",
    header: "Mode",
    cell: ({ row }) => <EnrichmentModeCell item={row.original} />,
    enableSorting: false,
    enableColumnFilter: false,
  },
  {
    id: "source",
    header: "Source",
    cell: ({ row }) => <EnrichmentSourceCell item={row.original} />,
    enableSorting: false,
    enableColumnFilter: false,
  },
];

/** `attempts` / `parks` — the two job-health columns. */
const ENRICHMENT_HEALTH_COLUMNS: ColumnDef<PipelineItem>[] = [
  {
    id: "attempt_count",
    accessorKey: "attempt_count",
    header: "Attempts",
    cell: ({ row }) => <AttemptsCell item={row.original} />,
    enableColumnFilter: false,
  },
  {
    id: "parks",
    header: "Parks",
    cell: ({ row }) => <ParksCell item={row.original} />,
    enableSorting: false,
    enableColumnFilter: false,
  },
];

/** `final_confidence` — a scrape-only score; enrichment confidence is per-field. */
const CONFIDENCE_COLUMN: ColumnDef<PipelineItem> = {
  id: "final_confidence",
  accessorKey: "final_confidence",
  header: "Confidence",
  cell: ({ row }) => {
    const raw = row.original.final_confidence;
    if (raw === null || raw === undefined || raw === "") {
      return <span className="text-muted-foreground">—</span>;
    }
    const value = Number(raw);
    if (Number.isNaN(value)) {
      return <span className="text-muted-foreground">—</span>;
    }
    return (
      <span
        className={`font-mono text-xs px-1.5 py-0.5 rounded ${confidenceTint(value)}`}
      >
        {Math.round(value * 100)}%
      </span>
    );
  },
  enableColumnFilter: false,
};

export const useItemsColumns = (options?: { variant?: ItemsColumnVariant }) => {
  // Destructured to strings before they reach the memo deps: callers pass an
  // object literal, which is a fresh reference every render, and an unstable
  // `columns` array resets @refinedev/react-table to page 1 on every render
  // while a filter is active (the bug the `showRef` dance below exists for).
  const variant: ItemsColumnVariant = options?.variant ?? "default";
  const { members } = useMembers();
  // Refine's `useNavigation().show` is a fresh function on every render. Keeping
  // it in the columns memo's deps would rebuild the array each render, and
  // @refinedev/react-table resets the table to page 1 whenever the `columns`
  // reference changes while a filter is active — which the HITL queue always has
  // (status=hitl). That made the queue impossible to page past 1. Hold `show` in
  // a ref so navigation stays current without destabilising the columns.
  const { show } = useNavigation();
  const showRef = React.useRef(show);
  showRef.current = show;
  return React.useMemo<ColumnDef<PipelineItem>[]>(
    () => [
      ...(variant === "enrichment"
        ? ENRICHMENT_LEADING_COLUMNS
        : SCRAPE_LEADING_COLUMNS),
      {
        id: "status",
        accessorKey: "status",
        header: "Status",
        cell: ({ row }) => <ItemStatusBadge status={row.original.status} />,
        meta: { filterType: "select", options: STATUS_OPTIONS },
      },
      {
        id: "current_stage",
        accessorKey: "current_stage",
        header: "Stage",
        cell: (props) =>
          props.getValue() ? (
            <ColoredBadge getValue={props.getValue} />
          ) : (
            <span className="text-muted-foreground">—</span>
          ),
        enableColumnFilter: false,
      },
      ...(variant === "enrichment" ? ENRICHMENT_HEALTH_COLUMNS : []),
      {
        id: "final_outcome",
        accessorKey: "final_outcome",
        header: "Outcome",
        cell: (props) =>
          props.getValue() ? (
            <ColoredBadge getValue={props.getValue} />
          ) : (
            <span className="text-muted-foreground">—</span>
          ),
        meta: {
          filterType: "select",
          options:
            variant === "enrichment"
              ? ENRICHMENT_OUTCOME_OPTIONS
              : SCRAPE_OUTCOME_OPTIONS,
        },
      },
      {
        id: "hitl_reason",
        accessorKey: "hitl_reason",
        header: "HITL reason",
        cell: ({ row }) =>
          row.original.hitl_reason ? (
            <HitlReasonBadge reason={row.original.hitl_reason} />
          ) : (
            <span className="text-muted-foreground">—</span>
          ),
        meta: {
          filterType: "select",
          // No queue-specific widening. `/tina/hitl` re-narrows its own page to
          // the scrape classes (see `listsHitlRows`), so offering
          // `refresh_review` there would filter over rows the queue itself
          // hides. It is offered on `/tina/refresh-review` instead, which reads
          // `v_refresh_jobs` and can page and count them properly.
          options:
            variant === "enrichment"
              ? ENRICHMENT_HITL_REASON_OPTIONS
              : SCRAPE_HITL_REASON_OPTIONS,
        },
      },
      ...(variant === "enrichment" ? [] : [CONFIDENCE_COLUMN]),
      {
        id: "created_at",
        accessorKey: "created_at",
        header: "Created",
        accessorFn: (row) => dayjs(row.created_at),
        cell: function render({ getValue }: any) {
          return makeTooltip(
            getValue().format("DD/MM/YYYY HH:mm"),
            getValue().format("ddd, MMM D, YYYY HH:mm"),
          );
        },
        enableColumnFilter: false,
      },
      {
        id: "run",
        header: "Run",
        enableSorting: false,
        enableColumnFilter: false,
        cell: ({ row }) => {
          const item = row.original;
          const member = members.find((m) => m.id === item.run_triggered_by);
          const who = triggeredByLabel(
            item.run_triggered_by,
            item.run_trigger_type,
            members,
          );
          const when = item.run_created_at
            ? dayjs(item.run_created_at).format("MMM D, HH:mm")
            : "";
          return makeTooltip(
            <button
              type="button"
              onClick={(e) => {
                e.stopPropagation();
                showRef.current("tina-runs", item.run_id);
              }}
              className="block min-w-0 text-left hover:underline"
            >
              {member ? (
                <UserAvatar {...member} half />
              ) : (
                <span className="block max-w-[160px] truncate text-xs font-medium">
                  {who}
                </span>
              )}
              <span className="block text-[11px] text-muted-foreground">
                {when}
              </span>
            </button>,
            item.run_id,
          );
        },
      },
      {
        id: "actions",
        size: 70,
        header: "Actions",
        cell: ({ row }) => (
          <ShowButton
            size="sm"
            hideText
            recordItemId={row.original.id}
            resource="tina-items"
          />
        ),
        enableSorting: false,
        enableColumnFilter: false,
      },
    ],
    [members, variant],
  );
};

interface ItemContextSummary {
  title: string;
  subtitle?: string;
  href?: string;
}

function buildItemContext(item: PipelineItem): ItemContextSummary | null {
  const metadata = asRecord(item.metadata);
  const sourceUrl = itemSourceUrl(item);
  const lineage = itemLineageLabel(item);

  if (isDiscoverSeedItem(item)) {
    return {
      title: discoveredUrlLabel(itemDiscoveredCount(item)),
      subtitle: [lineage, contentTypeLabel(item.content_type)]
        .filter((value): value is string => Boolean(value))
        .join(" · "),
    };
  }

  if (item.content_type !== "cinema_screening") {
    if (!sourceUrl && !lineage) return null;
    return {
      title: itemSourceTitle(item),
      subtitle:
        [lineage, contentTypeLabel(item.content_type)]
          .filter((value): value is string => Boolean(value))
          .join(" · ") || undefined,
      href: sourceUrl ?? undefined,
    };
  }

  const title = firstText(
    metadata.movie_title,
    metadata.extracted_movie_title,
    metadata.resolved_movie_title,
  );
  const venue = firstText(metadata.venue_name, metadata.resolved_venue_name);
  const resolvedTitle = firstText(metadata.resolved_movie_title);
  const sourceMovieId = firstText(metadata.source_movie_id);
  const subtitle = [
    lineage,
    resolvedTitle && resolvedTitle !== title ? resolvedTitle : null,
    venue,
    sourceMovieId ? `source ${sourceMovieId}` : null,
  ]
    .filter((value): value is string => Boolean(value))
    .join(" · ");

  if (!title && !sourceUrl) return null;
  return {
    title: title ?? urlLabel(sourceUrl ?? null),
    subtitle: subtitle || undefined,
    href: sourceUrl ?? undefined,
  };
}

function urlLabel(url: string | null): string {
  if (!url) return "Cinema screening";
  return urlTitle(url);
}
